import {
  Badge,
  Button,
  ChannelBadge,
  ChannelFilter,
  buttonVariants,
  Card,
  cn,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SkeletonTable,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CircleDollarSign,
  ExternalLink,
  Pause,
  Play,
  Tags,
  TrendingUp,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { BulkMapFromSku } from "../../components/bulk-map-from-sku";
import { CopyButton } from "../../components/copy-button";
import { ListSearch } from "../../components/list-search";
import { listingSku, useChannels } from "../../lib/channel";
import { listCardClassName } from "../../lib/list-card";
import { listingSearchFields, matchesListQuery } from "../../lib/list-query";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { formatBRL, formatDateTime, formatPercent } from "../../lib/format";
import {
  LISTING_STATUS_LABELS,
  LISTING_STATUS_VARIANTS,
} from "../../lib/order-status";
import { secureAssetUrl } from "../../lib/url";

type ListingsSearch = {
  q?: string;
  provider?: "mercadolivre" | "shopee";
};

export const Route = createFileRoute("/_app/listings")({
  validateSearch: (search: Record<string, unknown>): ListingsSearch => ({
    q: typeof search.q === "string" && search.q.trim() ? search.q : undefined,
    provider:
      search.provider === "mercadolivre" || search.provider === "shopee"
        ? search.provider
        : undefined,
  }),
  staticData: { layout: "wide" },
  component: ListingsPage,
});

type HealthFilter = "all" | "sick";
type BulkListingAction = "pause" | "activate" | "apply_suggested";

const BULK_LISTING_LIMIT = 100;

const BULK_ACTION_COPY: Record<
  BulkListingAction,
  { title: string; verb: string; hint: string }
> = {
  pause: {
    title: "Pausar",
    verb: "pausados",
    hint: "Só anúncios ativos mudam. Os outros deste filtro são ignorados.",
  },
  activate: {
    title: "Ativar",
    verb: "ativados",
    hint: "Só anúncios pausados mudam. Os outros deste filtro são ignorados.",
  },
  apply_suggested: {
    title: "Aplicar preço",
    verb: "preços aplicados",
    hint: "Usa o piso de 30% e a sugestão do Mercado Livre. Sem preço, o anúncio é ignorado.",
  },
};

function ListingsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [accountId, setAccountId] = useState<string | null>(null);
  const [healthFilter, setHealthFilter] = useState<HealthFilter>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<BulkListingAction | null>(null);

  const accounts = useQuery({
    queryKey: ["ml-accounts"],
    queryFn: async () => unwrap(await api.ml.accounts.get()),
  });
  const channels = useChannels();

  const listings = useQuery({
    queryKey: ["listings", accountId, search.provider],
    queryFn: async () =>
      unwrap(
        await api.listings.get({
          query: {
            accountId: accountId ?? undefined,
            provider: search.provider,
          },
        }),
      ),
  });

  const rows = listings.data?.listings ?? [];
  const searched = rows.filter((listing) =>
    matchesListQuery(search.q, listingSearchFields(listing)),
  );
  const sickCount = searched.filter(
    (listing) => "health" in listing && listing.health?.sick,
  ).length;
  const visible =
    healthFilter === "sick"
      ? searched.filter((listing) => "health" in listing && listing.health?.sick)
      : searched;
  const writesEnabled = Boolean(listings.data?.writesEnabled);
  const selectedVisible = useMemo(
    () =>
      visible
        .filter((listing) => selected.has(listing.id))
        .slice(0, BULK_LISTING_LIMIT),
    [visible, selected],
  );
  const selectableIds = visible.slice(0, BULK_LISTING_LIMIT).map((row) => row.id);
  const allVisibleSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selected.has(id));

  const bulk = useMutation({
    mutationFn: async (action: BulkListingAction) =>
      unwrap(
        await api.listings.bulk.post({
          ids: selectedVisible.map((row) => row.id),
          action,
        }),
      ),
    onSuccess: (result, action) => {
      setConfirm(null);
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ["listings"] });
      const copy = BULK_ACTION_COPY[action];
      const parts = [`${result.applied} ${copy.verb}`];
      if (result.refused > 0) {
        parts.push(
          `${result.refused} recusado${result.refused === 1 ? "" : "s"} pelo ML`,
        );
      }
      if (result.skipped > 0) {
        parts.push(
          `${result.skipped} ignorado${result.skipped === 1 ? "" : "s"}`,
        );
      }
      toast.add({
        type: result.applied > 0 ? "success" : "error",
        title: result.applied > 0 ? parts.join(", ") : "Nenhum anúncio alterado",
        description:
          result.applied === 0
            ? parts.slice(1).join(", ") || copy.hint
            : result.errors[0]
              ? result.errors[0].error
              : undefined,
      });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível alterar os anúncios",
        description:
          error instanceof Error ? error.message : "Tente de novo em instantes.",
      });
    },
  });

  function toggleSelected(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else if (next.size < BULK_LISTING_LIMIT) next.add(id);
      return next;
    });
  }

  function toggleAllVisible() {
    setSelected((current) => {
      if (selectableIds.every((id) => current.has(id))) return new Set();
      return new Set(selectableIds);
    });
  }

  return (
    <>
      <PageHeader
        title="Anúncios"
        description={
          healthFilter === "sick"
            ? `${visible.length} doente${visible.length === 1 ? "" : "s"} de ${searched.length}`
            : search.q
              ? `${visible.length} de ${rows.length} anúncios`
              : `${rows.length} anúncios espelhados`
        }
        actions={
          <>
            <Link
              to="/demand"
              search={{
                accountId: accountId ?? undefined,
                categoryId: undefined,
              }}
              className={buttonVariants({ variant: "secondary", size: "sm" })}
            >
              <TrendingUp />
              Demanda
            </Link>
            <BulkMapFromSku compact />
            <Select
              value={accountId}
              itemToStringLabel={(value) =>
                value == null
                  ? "Todas as contas"
                  : (accounts.data?.accounts.find((account) => account.id === value)
                      ?.displayName ?? String(value))
              }
              onValueChange={(v) => setAccountId(v as string | null)}
            >
              <SelectTrigger placeholder="Todas as contas" />
              <SelectContent>
                <SelectItem value={null}>Todas as contas</SelectItem>
                {accounts.data?.accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />

      <ChannelFilter
        className="mb-3"
        value={search.provider}
        channels={channels.providers.map((provider) => ({ provider }))}
        onChange={(provider) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              provider:
                provider === "mercadolivre" || provider === "shopee"
                  ? provider
                  : undefined,
            }),
          })
        }
      />

      <Card>
        {listings.isLoading ? (
          <SkeletonTable rows={10} columns={writesEnabled ? 6 : 5} />
        ) : listings.isError ? (
          <ErrorState
            title="Não foi possível carregar os anúncios"
            error={listings.error}
            retrying={listings.isFetching}
            onRetry={() => void listings.refetch()}
          />
        ) : listings.data && listings.data.listings.length > 0 ? (
          <>
          <div className="flex flex-col gap-2 border-b border-line px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-1">
              <FilterChip
                selected={healthFilter === "all"}
                onClick={() => setHealthFilter("all")}
              >
                Todos
              </FilterChip>
              <FilterChip
                selected={healthFilter === "sick"}
                onClick={() => setHealthFilter("sick")}
              >
                Doentes
                {sickCount > 0 ? (
                  <span className="font-mono opacity-70">{sickCount}</span>
                ) : null}
              </FilterChip>
            </div>
            <ListSearch
              value={search.q ?? ""}
              placeholder="Título, MLB ou SKU"
              onValueChange={(value) =>
                void navigate({
                  search: (prev) => ({
                    ...prev,
                    q: value.trim() || undefined,
                  }),
                })
              }
            />
          </div>
          {writesEnabled ? (
            <div className="flex flex-col gap-2 border-b border-line px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-2xs text-ink-muted">
                {selectedVisible.length === 0
                  ? "Selecione anúncios neste filtro"
                  : `${selectedVisible.length} neste filtro${visible.length > BULK_LISTING_LIMIT ? ` · até ${BULK_LISTING_LIMIT}` : ""}`}
              </p>
              <div className="flex flex-wrap gap-1.5">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={selectedVisible.length === 0}
                  onClick={() => setConfirm("pause")}
                >
                  <Pause />
                  Pausar
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={selectedVisible.length === 0}
                  onClick={() => setConfirm("activate")}
                >
                  <Play />
                  Ativar
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={selectedVisible.length === 0}
                  onClick={() => setConfirm("apply_suggested")}
                >
                  <CircleDollarSign />
                  Aplicar preço
                </Button>
              </div>
            </div>
          ) : null}
          {visible.length === 0 ? (
            <EmptyState
              icon={<Tags />}
              title={
                search.q
                  ? "Nenhum anúncio nesta busca"
                  : "Nenhum anúncio doente"
              }
              description={
                search.q
                  ? "Tente o título, o MLB ou o SKU do canal."
                  : "Os que precisam de SKU, estoque, visitas ou correção do Mercado Livre aparecem aqui."
              }
            />
          ) : (
          <>
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {writesEnabled ? (
                  <TableHead className="w-10 px-2">
                    <RowCheck
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                      label="Selecionar todos deste filtro"
                    />
                  </TableHead>
                ) : null}
                <TableHead>Anúncio</TableHead>
                <TableHead>Conta</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Preço</TableHead>
                <TableHead className="text-right">Margem</TableHead>
                <TableHead className="text-right">Conversão</TableHead>
                <TableHead className="text-right">Estoque</TableHead>
                <TableHead className="text-right">Sincronizado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((listing) => {
                const thumbnailUrl = secureAssetUrl(listing.thumbnailUrl);
                const sku = listingSku(listing);
                const extraSkus =
                  (listing.variationSkus?.length ?? 0) > 1
                    ? ` +${listing.variationSkus!.length - (listing.sellerSku ? 0 : 1)}`
                    : "";
                return (
                <TableRow
                  key={listing.id}
                  data-state={selected.has(listing.id) ? "selected" : undefined}
                  className="relative cursor-pointer"
                >
                  {writesEnabled ? (
                    <TableCell className="relative z-10 px-2">
                      <RowCheck
                        checked={selected.has(listing.id)}
                        onChange={() => toggleSelected(listing.id)}
                        label={`Selecionar ${listing.title}`}
                      />
                    </TableCell>
                  ) : null}
                  <TableCell className="min-w-0">
                    <Link
                      to="/listings/$id"
                      params={{ id: listing.id }}
                      className="absolute inset-0 z-0"
                      aria-label={listing.title}
                    />
                    <div className="pointer-events-none flex items-center gap-2.5">
                      {thumbnailUrl && (
                        <img
                          src={thumbnailUrl}
                          alt=""
                          className="size-8 shrink-0 rounded-control border border-line object-cover"
                          loading="lazy"
                        />
                      )}
                      <div className="min-w-0">
                        <p
                          className="truncate font-medium text-ink hover:text-ember"
                          title={listing.title}
                        >
                          {listing.title}
                        </p>
                        <p className="pointer-events-auto relative z-10 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-2xs text-ink-muted">
                          <span className="inline-flex items-center gap-1">
                            {listing.externalItemId}
                            <CopyButton
                              value={listing.externalItemId}
                              label="Copiar ID do anúncio"
                            />
                            {listing.permalink && (
                              <a
                                href={listing.permalink}
                                target="_blank"
                                rel="noreferrer"
                                className="text-ink-faint transition-colors hover:text-ember"
                              >
                                <ExternalLink className="size-3" />
                              </a>
                            )}
                          </span>
                          {sku ? (
                            <span className="inline-flex items-center gap-1">
                              {sku}
                              {extraSkus}
                              <CopyButton
                                value={sku}
                                label="Copiar SKU do canal"
                              />
                            </span>
                          ) : null}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="pointer-events-none">
                    <ChannelBadge
                      provider={listing.accountProvider}
                      nickname={listing.accountNickname}
                    />
                  </TableCell>
                  <TableCell className="pointer-events-none">
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <Badge
                        variant={LISTING_STATUS_VARIANTS[listing.status] ?? "neutral"}
                        dot
                      >
                        {LISTING_STATUS_LABELS[listing.status] ?? listing.status}
                      </Badge>
                      {"health" in listing && listing.health ? (
                        <ListingHealthMark health={listing.health} />
                      ) : null}
                      {"buyBox" in listing && listing.buyBox?.lost ? (
                        <Tooltip content="Perdeu o card do catálogo">
                          <Badge
                            variant="danger"
                            className="pointer-events-auto relative z-10"
                          >
                            Card
                          </Badge>
                        </Tooltip>
                      ) : null}
                      {listing.logisticType === "fulfillment" && (
                        <Tooltip content="Logística Full — estoque gerenciado pelo canal, sem sincronização automática">
                          <Badge variant="accent" className="pointer-events-auto">
                            Full
                          </Badge>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="pointer-events-none text-right font-mono text-ink">
                    {formatBRL(listing.price)}
                  </TableCell>
                  <TableCell className="pointer-events-none text-right font-mono">
                    {"product" in listing && listing.product?.margin != null ? (
                      <Tooltip
                        content={
                          listing.product.profit != null
                            ? `${formatBRL(listing.product.profit)} por unidade após o custo do SKU`
                            : "Cadastre o custo do SKU em Estoque"
                        }
                      >
                        <span className="pointer-events-auto">
                          {formatPercent(listing.product.margin)}
                        </span>
                      </Tooltip>
                    ) : (
                      <span className="text-ink-muted">—</span>
                    )}
                  </TableCell>
                  <TableCell className="pointer-events-none text-right font-mono">
                    {"demand" in listing && listing.demand?.conversion != null ? (
                      <Tooltip
                        content={
                          listing.demand.dropped
                            ? `Queda brusca · ${listing.demand.sold30d ?? 0} vendas / ${listing.demand.visits30d ?? 0} visitas em 30 dias`
                            : `${listing.demand.sold30d ?? 0} vendas / ${listing.demand.visits30d ?? 0} visitas em 30 dias`
                        }
                      >
                        <span
                          className={`pointer-events-auto ${listing.demand.dropped ? "text-danger" : ""}`}
                        >
                          {formatPercent(listing.demand.conversion)}
                        </span>
                      </Tooltip>
                    ) : (
                      <span className="text-ink-muted">—</span>
                    )}
                  </TableCell>
                  <TableCell className="pointer-events-none text-right font-mono">
                    {listing.availableQuantity}
                  </TableCell>
                  <TableCell className="pointer-events-none whitespace-nowrap text-right text-2xs text-ink-muted">
                    {listing.syncedAt ? formatDateTime(listing.syncedAt) : "—"}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
          <ul className="divide-y divide-line md:hidden">
            {visible.map((listing) => (
              <li key={listing.id}>
                <ListingCard
                  listing={listing}
                  selectable={writesEnabled}
                  selected={selected.has(listing.id)}
                  onToggle={() => toggleSelected(listing.id)}
                />
              </li>
            ))}
          </ul>
          </>
          )}
          </>
        ) : (
          <EmptyState
            icon={<Tags />}
            title="Nenhum anúncio importado"
            description="Importe os anúncios das suas contas na página de Conexões."
          />
        )}
      </Card>
      <Dialog
        open={confirm != null}
        onOpenChange={(open) => {
          if (!open && !bulk.isPending) setConfirm(null);
        }}
      >
        <DialogContent
          title={
            confirm
              ? `${BULK_ACTION_COPY[confirm].title} ${selectedVisible.length} anúncio${selectedVisible.length === 1 ? "" : "s"}?`
              : "Ação em massa"
          }
          description={
            confirm
              ? `${BULK_ACTION_COPY[confirm].hint} Não dá para desfazer — o inverso é outra ação em massa.`
              : undefined
          }
        >
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={bulk.isPending}
              onClick={() => setConfirm(null)}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              disabled={confirm == null || selectedVisible.length === 0}
              loading={bulk.isPending}
              onClick={() => {
                if (confirm) bulk.mutate(confirm);
              }}
            >
              Confirmar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ListingCard({
  listing,
  selectable,
  selected,
  onToggle,
}: {
  listing: {
    id: string;
    title: string;
    externalItemId: string;
    thumbnailUrl?: string | null;
    sellerSku?: string | null;
    variationSkus?: Array<{ sku: string | null }> | null;
    accountNickname: string;
    status: string;
    price: string | number;
    availableQuantity: number;
    logisticType?: string | null;
    health?: {
      score: number;
      sick: boolean;
      summary: string;
      issues: Array<{ id: string; label: string }>;
    };
    buyBox?: { lost?: boolean } | null;
  };
  selectable?: boolean;
  selected?: boolean;
  onToggle?: () => void;
}) {
  const thumbnailUrl = secureAssetUrl(listing.thumbnailUrl);
  const sku = listingSku(listing);
  const extra =
    (listing.variationSkus?.length ?? 0) > 1
      ? ` +${listing.variationSkus!.length - (listing.sellerSku ? 0 : 1)}`
      : "";
  const sick = listing.health?.sick;

  const body = (
      <div className="flex gap-3">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="size-10 shrink-0 rounded-control border border-line object-cover"
            loading="lazy"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <p className="line-clamp-2 font-medium text-ink">{listing.title}</p>
            <Badge
              variant={
                sick
                  ? "danger"
                  : (LISTING_STATUS_VARIANTS[listing.status] ?? "neutral")
              }
              dot
            >
              {sick
                ? "Doente"
                : (LISTING_STATUS_LABELS[listing.status] ?? listing.status)}
            </Badge>
          </div>
          <p className="mt-1 font-mono text-2xs text-ink-muted">
            {listing.externalItemId}
            {sku ? ` · ${sku}${extra}` : ""}
          </p>
          <p className="mt-1 text-2xs text-ink-faint">
            {formatBRL(listing.price)} · {listing.availableQuantity} un. ·{" "}
            {listing.accountNickname}
            {listing.logisticType === "fulfillment" ? " · Full" : ""}
            {listing.buyBox?.lost ? " · Card" : ""}
          </p>
        </div>
      </div>
  );

  if (!selectable) {
    return (
      <Link
        to="/listings/$id"
        params={{ id: listing.id }}
        className={listCardClassName}
      >
        {body}
      </Link>
    );
  }

  return (
    <div
      className={cn(
        "flex items-stretch",
        selected ? "bg-ember-subtle" : undefined,
      )}
    >
      <div className="flex items-center pl-2">
        <RowCheck
          checked={Boolean(selected)}
          onChange={() => onToggle?.()}
          label={`Selecionar ${listing.title}`}
        />
      </div>
      <Link
        to="/listings/$id"
        params={{ id: listing.id }}
        className={cn(listCardClassName, "min-w-0 flex-1")}
      >
        {body}
      </Link>
    </div>
  );
}

function RowCheck({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label
      className="relative z-10 flex size-8 cursor-pointer items-center justify-center"
      onClick={(event) => event.stopPropagation()}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="size-4 accent-ember"
        aria-label={label}
      />
    </label>
  );
}

function FilterChip({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        "inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-2xs font-medium",
        "transition-transform duration-150 ease-swift active:scale-[0.97]",
        selected
          ? "bg-ember-subtle text-ember-strong"
          : "text-ink-secondary [@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken",
      )}
    >
      {children}
    </button>
  );
}

function ListingHealthMark({
  health,
}: {
  health: {
    score: number;
    sick: boolean;
    summary: string;
    issues: Array<{ id: string; label: string }>;
  };
}) {
  const tip =
    health.issues.length > 0
      ? health.issues.map((issue) => issue.label).join(" · ")
      : health.summary;
  return (
    <Tooltip content={tip}>
      <span
        className={cn(
          "pointer-events-auto relative z-10 inline-flex items-center gap-1 font-mono text-2xs tabular-nums",
          health.sick ? "text-danger" : "text-ink-muted",
        )}
      >
        <span
          className={cn(
            "size-1.5 rounded-full",
            health.sick ? "bg-danger" : "bg-success",
          )}
          aria-hidden
        />
        {health.score}
      </span>
    </Tooltip>
  );
}
